<script setup>
import { computed, defineAsyncComponent, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { OnClickOutside } from '@vueuse/components';
import Draggable from 'vuedraggable';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { MoreHorizontalCircle01Icon } from '@hugeicons/core-free-icons';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

const CATEGORY_LIFECYCLE = 'lifecycle_stage';
const CATEGORY_LOST = 'lost_stage';
const LIFECYCLE_COLUMN_ICON = '🏆';
const LOST_COLUMN_ICON = '😔';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const draftStages = ref([]);
const showDescriptions = ref({});
const activeEmojiStageId = ref(null);
const activeOptionsStageId = ref(null);
const dragging = ref(false);

const uiFlags = computed(() => getters['lifecycleStages/getUIFlags'].value);
const settings = computed(() => getters['lifecycleStages/getSettings'].value);
const records = computed(() => getters['lifecycleStages/getStages'].value);

const sortStages = stages => {
  return [...stages].sort((a, b) => {
    if (a.category === b.category) return a.position - b.position;
    return a.category === CATEGORY_LIFECYCLE ? -1 : 1;
  });
};

const syncDraftStages = () => {
  draftStages.value = sortStages(records.value).map(stage => ({ ...stage }));
  showDescriptions.value = records.value.reduce((acc, stage) => {
    acc[stage.id] = false;
    return acc;
  }, {});
};

const stagesByCategory = category => {
  return draftStages.value.filter(stage => stage.category === category);
};

const updateCategoryOrder = (category, nextStages) => {
  const reorderedForCategory = nextStages.map((stage, index) => ({
    ...stage,
    position: index + 1,
  }));
  const otherStages = draftStages.value.filter(
    stage => stage.category !== category
  );
  draftStages.value = sortStages([...otherStages, ...reorderedForCategory]);
};

const lifecycleStages = computed({
  get: () => stagesByCategory(CATEGORY_LIFECYCLE),
  set: nextStages => updateCategoryOrder(CATEGORY_LIFECYCLE, nextStages),
});
const lostStages = computed({
  get: () => stagesByCategory(CATEGORY_LOST),
  set: nextStages => updateCategoryOrder(CATEGORY_LOST, nextStages),
});

const nextDraftId = () =>
  `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

const addStage = category => {
  draftStages.value.push({
    id: nextDraftId(),
    icon: category === CATEGORY_LIFECYCLE ? '⭐' : '😔',
    name: '',
    description: '',
    position: stagesByCategory(category).length + 1,
    category,
    is_default: false,
    is_won: false,
    is_system: false,
    isNew: true,
  });
};

const toggleDescription = stage => {
  showDescriptions.value[stage.id] = !showDescriptions.value[stage.id];
};

const setExclusiveFlag = (stage, flag) => {
  draftStages.value = draftStages.value.map(item => ({
    ...item,
    [flag]: item.id === stage.id,
  }));
};

const closeStageOptionsMenu = () => {
  activeOptionsStageId.value = null;
};

const toggleStageOptionsMenu = stage => {
  activeEmojiStageId.value = null;
  activeOptionsStageId.value =
    activeOptionsStageId.value === stage.id ? null : stage.id;
};

const toggleDefaultStage = stage => {
  if (stage.is_default) {
    draftStages.value = draftStages.value.map(item => ({
      ...item,
      is_default: item.id === stage.id ? false : item.is_default,
    }));
  } else {
    setExclusiveFlag(stage, 'is_default');
  }
};

const stageMenuSections = stage => {
  const sections = [];

  if (stage.category === CATEGORY_LIFECYCLE) {
    sections.push({
      items: [
        {
          action: 'toggleDefault',
          value: 'toggleDefault',
          label: stage.is_default
            ? t('LIFECYCLE_STAGES.SETUP.REMOVE_DEFAULT')
            : t('LIFECYCLE_STAGES.SETUP.SET_AS_DEFAULT'),
          icon: 'i-lucide-user-cog',
        },
      ],
    });
  }

  if (!stage.is_system) {
    sections.push({
      items: [
        {
          action: 'delete',
          value: 'delete',
          label: t('LIFECYCLE_STAGES.SETUP.DELETE_STAGE'),
          icon: 'i-lucide-trash-2',
        },
      ],
    });
  }

  return sections;
};

const selectEmoji = (stage, emoji) => {
  stage.icon = emoji || '•';
  activeEmojiStageId.value = null;
};

const onDragEnd = () => {
  dragging.value = false;
};

const removeStage = async stage => {
  if (stage.is_system) return;

  if (stage.isNew) {
    draftStages.value = draftStages.value.filter(item => item.id !== stage.id);
    return;
  }

  try {
    await store.dispatch('lifecycleStages/delete', stage.id);
    useAlert(t('LIFECYCLE_STAGES.API.DELETE_SUCCESS'));
    syncDraftStages();
  } catch (error) {
    useAlert(error.message || t('LIFECYCLE_STAGES.API.DELETE_ERROR'));
  }
};

const onStageMenuAction = ({ action }, stage) => {
  closeStageOptionsMenu();

  if (action === 'toggleDefault') {
    toggleDefaultStage(stage);
  } else if (action === 'delete') {
    removeStage(stage);
  }
};

const normalizedStage = stage => {
  const position =
    stagesByCategory(stage.category).findIndex(item => item.id === stage.id) +
    1;
  return {
    icon: stage.icon || '•',
    name: stage.name,
    description: stage.description,
    position,
    category: stage.category,
    is_default: Boolean(stage.is_default),
    is_won: Boolean(stage.is_won),
  };
};

const saveStages = async () => {
  try {
    const stagesToSave = sortStages(draftStages.value).filter(stage =>
      stage.name?.trim()
    );
    const orderedStages = [
      ...stagesToSave.filter(stage => !stage.is_default && !stage.is_won),
      ...stagesToSave.filter(stage => stage.is_default || stage.is_won),
    ];

    // Save exclusive flags last so default/won uniqueness is not violated while switching stages.
    await orderedStages.reduce(async (previousSave, stage) => {
      await previousSave;
      const payload = normalizedStage(stage);
      if (stage.isNew) {
        return store.dispatch('lifecycleStages/create', payload);
      }
      return store.dispatch('lifecycleStages/update', {
        id: stage.id,
        ...payload,
      });
    }, Promise.resolve());
    await store.dispatch('lifecycleStages/get');
    syncDraftStages();
    useAlert(t('LIFECYCLE_STAGES.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('LIFECYCLE_STAGES.API.SAVE_ERROR'));
  }
};

const updateLifecycleSetting = async enabled => {
  try {
    await store.dispatch('lifecycleStages/updateSettings', { enabled });
  } catch (error) {
    useAlert(error.message || t('LIFECYCLE_STAGES.API.SETTINGS_ERROR'));
  }
};

const lifecycleEnabled = computed({
  get: () => Boolean(settings.value.enabled),
  set: value => updateLifecycleSetting(value),
});

onBeforeMount(async () => {
  await store.dispatch('lifecycleStages/get');
  syncDraftStages();
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('LIFECYCLE_STAGES.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('LIFECYCLE_STAGES.HEADER')"
        :description="$t('LIFECYCLE_STAGES.DESCRIPTION')"
        :link-text="$t('LIFECYCLE_STAGES.LEARN_MORE')"
        feature-name="lifecycle-stages"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <div
          class="flex items-center justify-between gap-4 p-4 border rounded-lg border-n-strong bg-n-alpha-1"
        >
          <span class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('LIFECYCLE_STAGES.TOGGLE.TITLE') }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ $t('LIFECYCLE_STAGES.TOGGLE.DESCRIPTION') }}
            </span>
          </span>
          <Switch v-model="lifecycleEnabled" size="md" />
        </div>

        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between gap-4">
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ $t('LIFECYCLE_STAGES.SETUP.TITLE') }}
            </h3>
            <div class="flex items-center gap-2">
              <Button
                :label="$t('LIFECYCLE_STAGES.SETUP.CANCEL')"
                size="sm"
                slate
                faded
                @click="syncDraftStages"
              />
              <Button
                :label="$t('LIFECYCLE_STAGES.SETUP.SAVE')"
                size="sm"
                :is-loading="uiFlags.isUpdating || uiFlags.isCreating"
                @click="saveStages"
              />
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 2xl:grid-cols-2">
            <section class="overflow-visible border rounded-lg border-n-strong">
              <div class="flex items-start gap-3 p-4 bg-n-slate-4 rounded-t-lg">
                <span class="text-xl">{{ LIFECYCLE_COLUMN_ICON }}</span>
                <div class="flex flex-col gap-1">
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{ $t('LIFECYCLE_STAGES.COLUMNS.LIFECYCLE.TITLE') }}
                  </h4>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ $t('LIFECYCLE_STAGES.COLUMNS.LIFECYCLE.DESCRIPTION') }}
                  </p>
                </div>
              </div>
              <Draggable
                v-model="lifecycleStages"
                class="flex flex-col gap-3 p-4"
                animation="200"
                handle=".stage-drag-handle"
                item-key="id"
                @start="dragging = true"
                @end="onDragEnd"
              >
                <template #item="{ element: stage, index }">
                  <div class="flex min-w-0 flex-1 items-start gap-2">
                    <button
                      type="button"
                      class="stage-drag-handle i-lucide-grip-vertical mt-5 flex h-[30px] w-5 shrink-0 cursor-grab items-center justify-center text-base text-n-slate-12 active:cursor-grabbing"
                      :aria-label="$t('LIFECYCLE_STAGES.SETUP.DRAG_STAGE')"
                    />
                    <OnClickOutside
                      class="relative mt-5 shrink-0"
                      @trigger="activeEmojiStageId = null"
                    >
                      <Button
                        size="sm"
                        slate
                        faded
                        type="button"
                        class="!h-[30px] !w-[30px] !min-w-[30px] !p-0"
                        @click="
                          activeEmojiStageId =
                            activeEmojiStageId === stage.id ? null : stage.id
                        "
                      >
                        <span
                          class="flex size-full items-center justify-center text-xl leading-none"
                        >
                          {{ stage.icon }}
                        </span>
                      </Button>
                      <EmojiInput
                        v-if="activeEmojiStageId === stage.id"
                        class="!top-9 !left-0 !right-auto"
                        show-remove-button
                        :on-click="emoji => selectEmoji(stage, emoji)"
                      />
                    </OnClickOutside>
                    <div class="flex min-w-0 flex-1 flex-col gap-1">
                      <span class="text-xs font-medium text-n-slate-11">
                        {{
                          $t('LIFECYCLE_STAGES.SETUP.STAGE_LABEL', {
                            n: index + 1,
                          })
                        }}
                      </span>
                      <div
                        class="flex box-content h-[30px] items-center gap-2 rounded-lg border border-n-strong bg-n-alpha-1 px-3"
                      >
                        <input
                          v-model="stage.name"
                          :aria-label="
                            $t('LIFECYCLE_STAGES.SETUP.STAGE_LABEL', {
                              n: index + 1,
                            })
                          "
                          class="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-n-slate-12 focus:outline-none"
                        />
                        <span
                          v-if="stage.is_default"
                          class="shrink-0 whitespace-nowrap rounded-md border border-n-blue-7 bg-n-blue-5 px-2 py-0.5 text-xs text-n-blue-11"
                        >
                          {{ $t('LIFECYCLE_STAGES.SETUP.DEFAULT_STAGE') }}
                        </span>
                        <span
                          v-if="stage.is_won"
                          class="shrink-0 whitespace-nowrap rounded-md border border-n-teal-7 bg-n-teal-5 px-2 py-0.5 text-xs text-n-teal-11"
                        >
                          {{ $t('LIFECYCLE_STAGES.SETUP.WON_STAGE') }}
                        </span>
                      </div>
                      <input
                        v-if="showDescriptions[stage.id]"
                        v-model="stage.description"
                        :aria-label="$t('LIFECYCLE_STAGES.FORM.DESCRIPTION')"
                        :placeholder="
                          $t('LIFECYCLE_STAGES.FORM.DESCRIPTION_PLACEHOLDER')
                        "
                        class="box-content h-[30px] w-full rounded-lg border border-n-strong bg-n-alpha-1 px-3 text-sm text-n-slate-12 focus:outline-none"
                      />
                      <button
                        type="button"
                        class="w-fit text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
                        @click="toggleDescription(stage)"
                      >
                        {{
                          showDescriptions[stage.id]
                            ? $t('LIFECYCLE_STAGES.SETUP.HIDE_DESCRIPTION')
                            : $t('LIFECYCLE_STAGES.SETUP.SHOW_DESCRIPTION')
                        }}
                      </button>
                    </div>
                    <OnClickOutside
                      class="relative mt-5 shrink-0"
                      @trigger="closeStageOptionsMenu"
                    >
                      <div class="relative">
                        <Button
                          size="sm"
                          slate
                          faded
                          class="!h-[30px] !w-[30px] !px-0"
                          :aria-label="
                            $t('LIFECYCLE_STAGES.SETUP.STAGE_OPTIONS')
                          "
                          @click.stop="toggleStageOptionsMenu(stage)"
                        >
                          <template #icon>
                            <HugeIcon
                              :icon="MoreHorizontalCircle01Icon"
                              :size="18"
                              class="shrink-0"
                            />
                          </template>
                        </Button>
                        <DropdownMenu
                          v-if="activeOptionsStageId === stage.id"
                          :menu-sections="stageMenuSections(stage)"
                          icon-class="size-5"
                          label-class="text-base"
                          class="absolute end-0 top-full z-[100] mt-1 !min-w-0 w-max"
                          @action="payload => onStageMenuAction(payload, stage)"
                        />
                      </div>
                    </OnClickOutside>
                  </div>
                </template>
                <template #footer>
                  <Button
                    :label="$t('LIFECYCLE_STAGES.SETUP.ADD_STAGE')"
                    icon="i-lucide-plus"
                    slate
                    faded
                    @click="addStage(CATEGORY_LIFECYCLE)"
                  />
                </template>
              </Draggable>
            </section>

            <section class="overflow-visible border rounded-lg border-n-strong">
              <div class="flex items-start gap-3 p-4 bg-n-amber-5 rounded-t-lg">
                <span class="text-xl">{{ LOST_COLUMN_ICON }}</span>
                <div class="flex flex-col gap-1">
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{ $t('LIFECYCLE_STAGES.COLUMNS.LOST.TITLE') }}
                  </h4>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ $t('LIFECYCLE_STAGES.COLUMNS.LOST.DESCRIPTION') }}
                  </p>
                </div>
              </div>
              <Draggable
                v-model="lostStages"
                class="flex flex-col gap-3 p-4"
                animation="200"
                handle=".stage-drag-handle"
                item-key="id"
                @start="dragging = true"
                @end="onDragEnd"
              >
                <template #item="{ element: stage }">
                  <div class="flex min-w-0 flex-1 items-start gap-2">
                    <button
                      type="button"
                      class="stage-drag-handle i-lucide-grip-vertical mt-5 flex h-[30px] w-5 shrink-0 cursor-grab items-center justify-center text-base text-n-slate-12 active:cursor-grabbing"
                      :aria-label="$t('LIFECYCLE_STAGES.SETUP.DRAG_STAGE')"
                    />
                    <OnClickOutside
                      class="relative mt-5 shrink-0"
                      @trigger="activeEmojiStageId = null"
                    >
                      <Button
                        size="sm"
                        slate
                        faded
                        type="button"
                        class="!h-[30px] !w-[30px] !min-w-[30px] !p-0"
                        @click="
                          activeEmojiStageId =
                            activeEmojiStageId === stage.id ? null : stage.id
                        "
                      >
                        <span
                          class="flex size-full items-center justify-center text-xl leading-none"
                        >
                          {{ stage.icon }}
                        </span>
                      </Button>
                      <EmojiInput
                        v-if="activeEmojiStageId === stage.id"
                        class="!top-9 !left-0 !right-auto"
                        show-remove-button
                        :on-click="emoji => selectEmoji(stage, emoji)"
                      />
                    </OnClickOutside>
                    <div class="flex min-w-0 flex-1 flex-col gap-1">
                      <span class="text-xs font-medium text-n-slate-11">
                        {{ $t('LIFECYCLE_STAGES.FORM.NAME') }}
                      </span>
                      <input
                        v-model="stage.name"
                        :aria-label="$t('LIFECYCLE_STAGES.FORM.NAME')"
                        class="box-content h-[30px] w-full rounded-lg border border-n-strong bg-n-alpha-1 px-3 text-sm text-n-slate-12 focus:outline-none"
                      />
                      <input
                        v-if="showDescriptions[stage.id]"
                        v-model="stage.description"
                        :aria-label="$t('LIFECYCLE_STAGES.FORM.DESCRIPTION')"
                        :placeholder="
                          $t('LIFECYCLE_STAGES.FORM.DESCRIPTION_PLACEHOLDER')
                        "
                        class="box-content h-[30px] w-full rounded-lg border border-n-strong bg-n-alpha-1 px-3 text-sm text-n-slate-12 focus:outline-none"
                      />
                      <button
                        type="button"
                        class="w-fit text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
                        @click="toggleDescription(stage)"
                      >
                        {{
                          showDescriptions[stage.id]
                            ? $t('LIFECYCLE_STAGES.SETUP.HIDE_DESCRIPTION')
                            : $t('LIFECYCLE_STAGES.SETUP.SHOW_DESCRIPTION')
                        }}
                      </button>
                    </div>
                    <OnClickOutside
                      class="relative mt-5 shrink-0"
                      @trigger="closeStageOptionsMenu"
                    >
                      <div class="relative">
                        <Button
                          size="sm"
                          slate
                          faded
                          class="!h-[30px] !w-[30px] !px-0"
                          :aria-label="
                            $t('LIFECYCLE_STAGES.SETUP.STAGE_OPTIONS')
                          "
                          @click.stop="toggleStageOptionsMenu(stage)"
                        >
                          <template #icon>
                            <HugeIcon
                              :icon="MoreHorizontalCircle01Icon"
                              :size="18"
                              class="shrink-0"
                            />
                          </template>
                        </Button>
                        <DropdownMenu
                          v-if="activeOptionsStageId === stage.id"
                          :menu-sections="stageMenuSections(stage)"
                          icon-class="size-5"
                          label-class="text-base"
                          class="absolute end-0 top-full z-[100] mt-1 !min-w-0 w-max"
                          @action="payload => onStageMenuAction(payload, stage)"
                        />
                      </div>
                    </OnClickOutside>
                  </div>
                </template>
                <template #footer>
                  <Button
                    :label="$t('LIFECYCLE_STAGES.SETUP.ADD_STAGE')"
                    icon="i-lucide-plus"
                    slate
                    faded
                    @click="addStage(CATEGORY_LOST)"
                  />
                </template>
              </Draggable>
            </section>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
